import { app } from 'electron';
import { getSupabase } from './supabase/client';
import { requireUserId } from './supabase/auth';
import { t, uiLanguage } from './language';
import {
  checkFeatureRequest,
  FEATURE_REQUEST_COOLDOWN_MS,
  FEATURE_REQUEST_MAX_LENGTH,
  FEATURE_REQUEST_MIN_LENGTH,
  type FeatureRequestError,
} from './featureRequestRules';

/**
 * Ayarlar > Yardım al > Özellik iste. İstek Supabase'deki
 * `feature_requests` tablosuna düz metin olarak yazılır.
 *
 * Metin arayüzden geldiği için güvenilmiyor: burada normalleştirilip
 * denetleniyor (featureRequestRules.ts), veritabanı da aynı sınırları ve
 * sıklık sınırını ayrıca uyguluyor. Sürüm, platform ve dil arayüzden değil,
 * ana süreçten ekleniyor.
 */

let lastSubmittedAt = 0;

function errorText(error: FeatureRequestError): string {
  switch (error) {
    case 'empty':
      return t('İsteğini yazmalısın.', 'Please describe your request.');
    case 'too_short':
      return t(
        `Biraz daha ayrıntı ver — en az ${FEATURE_REQUEST_MIN_LENGTH} karakter.`,
        `Add a little more detail — at least ${FEATURE_REQUEST_MIN_LENGTH} characters.`
      );
    case 'too_long':
      return t(
        `İstek en fazla ${FEATURE_REQUEST_MAX_LENGTH} karakter olabilir.`,
        `Requests can be at most ${FEATURE_REQUEST_MAX_LENGTH} characters.`
      );
    case 'markup':
      return t(
        'İstek kod, betik ya da HTML içeremez. Fikrini düz metinle anlatır mısın?',
        'Requests can’t contain code, scripts or HTML. Could you describe your idea in plain text?'
      );
    case 'too_many_links':
      return t('Bir istekte en fazla 3 bağlantı olabilir.', 'A request can include at most 3 links.');
    case 'no_text':
      return t('İsteğini kelimelerle anlatır mısın?', 'Could you describe your request in words?');
  }
}

export async function submitFeatureRequest(value: unknown): Promise<void> {
  const check = checkFeatureRequest(value);
  if (!check.ok) throw new Error(errorText(check.error));

  if (Date.now() - lastSubmittedAt < FEATURE_REQUEST_COOLDOWN_MS) {
    throw new Error(
      t('Az önce bir istek gönderdin; birkaç saniye sonra tekrar dene.', 'You just sent a request — try again in a few seconds.')
    );
  }

  await requireUserId();
  const { error } = await getSupabase()
    .from('feature_requests')
    .insert({
      body: check.text,
      app_version: app.getVersion(),
      platform: `${process.platform}-${process.arch}`,
      ui_language: uiLanguage(),
    });

  if (error) {
    if (error.message.includes('feature_request_rate_limited')) {
      throw new Error(
        t('Bugün çok sayıda istek gönderdin. Biraz sonra tekrar dene.', 'You’ve sent a lot of requests today. Please try again later.')
      );
    }
    if (error.message.includes('feature_request_duplicate')) {
      throw new Error(t('Bu isteği zaten gönderdin — aldık.', 'You’ve already sent this request — we have it.'));
    }
    console.error('[featureRequests] kaydedilemedi:', error.code, error.message);
    throw new Error(t('İstek gönderilemedi. Biraz sonra tekrar dene.', 'Could not send the request. Please try again later.'));
  }

  lastSubmittedAt = Date.now();
}
